import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { login }   = useAuth();
  const navigate    = useNavigate();

  const [schoolName,   setSchoolName]   = useState('School ERP');
  const [logo,         setLogo]         = useState('');
  const [brandColor,   setBrandColor]   = useState('#6b21a8');

  useEffect(() => {
    fetch('/api/school-settings')
      .then(r => r.json())
      .then(d => {
        if (d.name)         setSchoolName(d.name);
        if (d.logo)         setLogo(d.logo);
        if (d.primaryColor) {
          setBrandColor(d.primaryColor);
          document.documentElement.style.setProperty('--brand-primary', d.primaryColor);
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password);
    if (result.success) navigate('/dashboard');
    else setError(result.error || 'Invalid email or password');
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex flex-col items-center justify-center flex-1 px-12 text-center"
        style={{ background: `linear-gradient(135deg, ${brandColor}ee, ${brandColor}bb)` }}>
        {logo
          ? <img src={logo} alt={schoolName} className="w-48 mb-8 drop-shadow-xl" onError={e => e.target.style.display='none'} />
          : <div className="w-32 h-32 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-6xl font-bold text-white mb-8">{schoolName[0]}</div>
        }
        <h1 className="text-4xl font-extrabold text-white tracking-tight">{schoolName.toUpperCase()}</h1>
        <p className="text-white text-opacity-70 mt-4 text-sm max-w-xs leading-relaxed opacity-75">
          Empowering education through smart school management
        </p>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            {logo && <img src={logo} alt={schoolName} className="w-32 mb-3" onError={e => e.target.style.display='none'} />}
            <h1 className="text-2xl font-extrabold" style={{ color: brandColor }}>{schoolName}</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-xl font-bold mb-1">Welcome back</h2>
            <p className="text-sm text-gray-500 mb-6">Sign in to your account to continue</p>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg mb-4 border border-red-100">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent transition"
                  style={{ '--tw-ring-color': brandColor }}
                  placeholder="Enter your email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Password</label>
                <div className="relative">
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 pr-10 transition"
                    placeholder="Enter your password" />
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: brandColor }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <LogIn size={18} />{loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
export default LoginPage;
